// MissionTooltip.jsx
import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { NULL_FUNCTION } from '../resource/defaults.js';
import FadeIn from '../style/FadeIn.js';
import { color } from '../style/variables.js';

export const MissionTooltip = ({
  visible = true,
  onClose = NULL_FUNCTION,
  autoCloseDuration = 5000,
  children,
  placement = 'bottomNav',
}) => {
  const tooltipRef = useRef(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!visible) return;

    const timer = setTimeout(handleClose, autoCloseDuration);

    const handleClick = e => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        handleClose();
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
    };
  }, [visible, autoCloseDuration, handleClose]);

  if (!visible) return null;

  return (
    <StyledMissionTooltip
      ref={tooltipRef}
      $placement={placement}
      onClick={e => e.stopPropagation()}
    >
      {children}
    </StyledMissionTooltip>
  );
};

MissionTooltip.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  autoCloseDuration: PropTypes.number,
  children: PropTypes.node,
  placement: PropTypes.string,
};

const StyledMissionTooltip = styled.div`
  position: absolute;
  ${({ $placement }) =>
    $placement === 'navHeader'
      ? `
        top: 100%;
        right: 1%;
      `
      : `
        bottom: calc(100% + 14px);
        right: 0;
      `}
  background: ${color.grey[900]};
  color: ${color.neutral[100]};
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  width: 125px;
  line-height: 1.5;
  font-weight: 400;
  letter-spacing: 0.012px;
  min-height: 48px;
  box-shadow: 0 2px 8px 0 ${color.neutral['0_alpha_20']};
  animation: 0.6s forwards ${FadeIn};
  &::after {
    content: '';
    position: absolute;
    ${({ $placement }) =>
      $placement === 'navHeader'
        ? `
       top: -1px;
       right: 12%;
       border-radius: 2px 0 0 0;
      `
        : `
       bottom: -8px;
       right: 6%;
       border-radius: 0 0 2px 0;
    `}
    width: 8px;
    height: 8px;
    background: ${color.grey[900]};
    transform: translateY(-50%) rotate(45deg);
  }
`;

export default MissionTooltip;
